type MouseAction = 'move' | 'down' | 'up' | 'click';

export type MouseState = {
  x: number;
  y: number;
  action: MouseAction;
  button: number;

  // Viewport size when recorded
  width: number;
  height: number;

  // Browser/device scale
  devicePixelRatio: number;

  // Position relative to viewport: 0..1
  normalizedX: number;
  normalizedY: number;
};

// ============================================================
// RECORDING
// ============================================================

let mouseState: MouseState = {
  x: 0,
  y: 0,
  action: 'move',
  button: 0,

  width: window.innerWidth,
  height: window.innerHeight,

  devicePixelRatio: window.devicePixelRatio,

  normalizedX: 0,
  normalizedY: 0,
};

function updateMouse(
  action: MouseAction,
  e: MouseEvent,
) {
  const width = window.innerWidth;
  const height = window.innerHeight;

  mouseState = {
    x: e.clientX,
    y: e.clientY,

    action,
    button: e.button,

    width,
    height,

    devicePixelRatio:
    window.devicePixelRatio,

    normalizedX:
      width > 0
        ? e.clientX / width
        : 0,

    normalizedY:
      height > 0
        ? e.clientY / height
        : 0,
  };
}

window.addEventListener('mousemove', e => {
  updateMouse('move', e);
});

window.addEventListener('mousedown', e => {
  updateMouse('down', e);
});

window.addEventListener('mouseup', e => {
  updateMouse('up', e);
});

window.addEventListener('click', e => {
  updateMouse('click', e);
});

export function getMouseTracker(): MouseState {
  return mouseState;
}

// ============================================================
// REPLAY
// ============================================================

type Point = {
  x: number;
  y: number;
};

let replayMouseData: MouseState | null = null;

let replayMouseElement: HTMLElement | null = null;

let currentPosition: Point | null = null;

let targetPosition: Point | null = null;

let animationFrame: number | null = null;

// ============================================================
// CREATE CURSOR
// ============================================================

export function createReplayMouse(): HTMLElement {
  const existing =
    document.getElementById('replay-mouse');

  if (existing) {
    replayMouseElement = existing;

    return existing;
  }

  const cursor =
    document.createElement('div');

  cursor.id = 'replay-mouse';

  Object.assign(cursor.style, {
    position: 'fixed',
    left: '0px',
    top: '0px',
    display: 'none',
    pointerEvents: 'none',
    zIndex: '2147483647',
    willChange: 'left, top',
  });

  document.body.appendChild(cursor);

  replayMouseElement = cursor;

  return cursor;
}

// ============================================================
// IMPORTANT:
// USE THE SAME COORDINATE SYSTEM AS RECORDING
// ============================================================

function getReplayPosition(
  data: MouseState,
): Point {
  return {
    x: data ? data.normalizedX * data.width : 0,
    y: data ? data.normalizedY * data.height : 0,
  };
}

// ============================================================
// SET CURSOR POSITION
// ============================================================

function setCursorPosition(
  cursor: HTMLElement,
  position: Point,
) {
  cursor.style.left =
    `${position.x}px`;

  cursor.style.top =
    `${position.y}px`;
}

// ============================================================
// ANIMATION
// ============================================================

function animateReplayMouse() {

  if (
    !replayMouseElement ||
    !targetPosition
  ) {
    animationFrame = null;

    return;
  }

  if (!currentPosition) {
    currentPosition = {
      x: targetPosition.x,
      y: targetPosition.y,
    };
  }

  // Smoothness
  const smoothing = 0.25;

  currentPosition.x +=
    (targetPosition.x - currentPosition.x)
    * smoothing;

  currentPosition.y +=
    (targetPosition.y - currentPosition.y)
    * smoothing;

  setCursorPosition(
    replayMouseElement,
    currentPosition,
  );

  animationFrame =
    requestAnimationFrame(
      animateReplayMouse,
    );
}

// ============================================================
// MOVE CURSOR
// ============================================================

export function moveReplayMouse(
  data: MouseState,
) {

  if (!data) {
    return;
  }

  replayMouseData = data;

  const cursor =
    createReplayMouse();

  cursor.style.display = 'block';

  const position =
    getReplayPosition(data);

  targetPosition = position;

  // First event
  if (!currentPosition) {

    currentPosition = {
      x: position.x,
      y: position.y,
    };

    setCursorPosition(
      cursor,
      currentPosition,
    );
  }

  // Click effect
  if (data.action === 'click') {

    cursor.classList.remove('click');

    // Force reflow
    void cursor.offsetWidth;

    cursor.classList.add('click');

    window.setTimeout(() => {
      cursor.classList.remove('click');
    }, 150);
  }

  // Only ONE animation loop
  if (animationFrame === null) {

    animationFrame =
      requestAnimationFrame(
        animateReplayMouse,
      );
  }
}

// ============================================================
// RESIZE / CTRL +/-
// ============================================================

function updateReplayPosition() {

  if (
    !replayMouseData ||
    !replayMouseElement
  ) {
    return;
  }

  // Recalculate using CURRENT viewport
  const position =
    getReplayPosition(
      replayMouseData,
    );

  targetPosition = position;
}

window.addEventListener(
  'resize',
  updateReplayPosition,
);

// ============================================================
// RESET
// ============================================================

export function removeReplayMouse() {
  const cursor = document.getElementById('replay-mouse');

  if (cursor) {
    cursor.remove();
  }

  replayMouseElement = null;
  replayMouseData = null;
  currentPosition = null;
  targetPosition = null;

  if (animationFrame !== null) {
    cancelAnimationFrame(animationFrame);
    animationFrame = null;
  }
}
